import React from 'react';
import { get, map, find, cloneDeep, merge, isEmpty, noop, split, trim, toString, parseInt } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

// local dependencies
import { epostPaymentInformationStep as EpostPaymentInformationStepView } from 'ui/views/widgets/epostPaymentInformationStep';
import { EpostPaymentInformationStepService } from 'services/epostPaymentInformationStep';
import { SERVICE_RESPONSE_STATUS, SIZES } from 'constants/common';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { getLabel } from 'utils/getLabel';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as successOverlay from 'utils/successOverlay';
import { GLOBAL_LABELS } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { EpostPaymentInformationStep } from 'ui/containers/EpostPaymentInformationStep';
 *
 * // render
 * <EpostPaymentInformationStep propName='propValue'/>
 */
export class _EpostPaymentInformationStep extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // check if URL container `step` query parameter.
        const queryParams = queryString.parse( this.props.location.search );

        // bind
        this.handlePaymentDefaultSelection = this.handlePaymentDefaultSelection.bind( this );
        this.handleInactivatePaymentInfo = this.handleInactivatePaymentInfo.bind( this );
        this.handleEditPaymentInfo = this.handleEditPaymentInfo.bind( this );
        this.handleNavActions = this.handleNavActions.bind( this );
        this.handleActivatePaymentInfo = this.handleActivatePaymentInfo.bind( this );
		this.submitActivatePaymentInfo = this.submitActivatePaymentInfo.bind( this );
        this.handleHsaFsaActionToggle = this.handleHsaFsaActionToggle.bind( this );

        // component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			isCallDispositionVisible: false,
            callReasonValidationState: false,
            origin: get( queryParams, 'origin', null ),
            memberId: get( this.props, 'requestPayload.context.memberId', null ),
            patientId: get( this.props, 'requestPayload.context.patientId', null )
		};

		// create service instance
		this.epostPaymentInformationStepService = new EpostPaymentInformationStepService();

		// start component data fetch
		this.fetchComponentData();
    }

	/*********************************************/

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'EpostPaymentInformationStep.fetchComponentData()' );

		// cancel ongoing request
		this.epostPaymentInformationStepService.cancel();

		// fetch component data
		const {
			status,
			data,
		} = await this.epostPaymentInformationStepService.fetchComponentData( {
			url: this.props.widget.url,
			data: this.props.requestPayload,
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
    }

    /**
     * update default payment selection
     */
    async handlePaymentDefaultSelection( { card } ) {
        log.debug( 'EpostPaymentInformationStep.handlePaymentDefaultSelection()' );

        // cancel ongoing request
        this.epostPaymentInformationStepService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // set the address default
        const { status, data } = await this.epostPaymentInformationStepService.setDefaultPaymentInfo( {
            url,
            data: merge( this.props.requestPayload, {
				data: {
					id: card.id
				},
			} ),
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedList = map( componentData.paymentOptions, item => {
                item.flags.isDefault = card.id === item.id ? true: false;
                return item;
            } );
            componentData.paymentOptions = updatedList;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );


        } else {
			
			// show error dialog
			if ( ! isEmpty( data.description ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
			} else {
				errorOverlay.open();
			}
        }
    }

    /**
     * update edit payment info
     */
    async handleEditPaymentInfo( { card, month, year } = {}, callback = noop ) {
        log.debug( 'EpostPaymentInformationStep.handleEditPaymentInfo()' );

        const action = find( this.state.componentData.modifyActions, { name: 'edit' } );
 
        // cancel ongoing request
        this.epostPaymentInformationStepService.cancel();

        //hide preloader
        pageLevelPreloader.show();

        // set the address default
        const { status, data } = await this.epostPaymentInformationStepService.submitEditedPaymentInfo( {
            url: action.url,
            data: merge( this.props.requestPayload, {
                data: {
                    id: card.id,
                    month: month,
                    year: year,
                }
            } ),
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update expiry date
            const componentData = cloneDeep( this.state.componentData );
            const cardItem = find( componentData.paymentOptions, item => {
                return card.id === item.id;
            } );
            cardItem.expDate = `${ month }/${ year }`;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, callback );

        } else {

            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
        
    }

    /**
	 * initiate activate payment info
	 */
	async handleActivatePaymentInfo( { card } ) {
		log.debug( 'EpostPaymentInformationStep.handleActivatePaymentInfo()' );

		let [ expiryMonth, expiryYear ] = split( card.expDate, '/' );
		expiryMonth = parseInt( trim( expiryMonth ) );
		expiryYear = parseInt( trim( expiryYear ) );

		const currentMonth = parseInt( moment().format( 'MM' ) );
		const currentYear = parseInt( moment().format( 'YY' ) );

		if ( expiryYear < currentYear || ( expiryYear === currentYear && expiryMonth < currentMonth ) ) {
			overlay.open( {
				settings: {
					modalType: OVERLAY_VIEWS.PAYMENT_CARD_ACTIVATION,
                    showCloseButton: false,
                    ignoreOutsideClick: false
				},
				data: {
					onActivateActionClick: this.submitActivatePaymentInfo,
					onSubmitEditedDate: this.handleEditPaymentInfo,
					onCancelClick: overlay.close,
                    onHsaFsaActionToggle: this.handleHsaFsaActionToggle,
					actions: this.state.componentData.modifyActions,
					requestPayload: this.props.requestPayload,
					card
				}
			} );
		} else {
			const action = find( this.state.componentData.modifyActions, { name: 'activate' } );
			this.submitActivatePaymentInfo( { url: action.url, id: card.id, month: toString( expiryMonth ), year: toString( expiryYear ) } );
		}
	}

	/**
	 * submit activate payment info
	 */
	async submitActivatePaymentInfo( { url, id, month, year } ) {
		log.debug( 'EpostPaymentInformationStep.submitActivatePaymentInfo()' );

		this.epostPaymentInformationStepService.cancel();

		overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.epostPaymentInformationStepService.submitActivatePaymentInfoRequest( {
			url,
			data: {
				context: {
                    memberId: this.props.requestPayload.context.memberId,
                    patientId: this.props.requestPayload.context.patientId,
                    journey: this.props.requestPayload.context.journey
                },
				data: {
					id,
					month,
					year
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isActivated = true;

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			} );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}

    /**
     * update delete payment info
     */
     async handleInactivatePaymentInfo( { card } ) {
		log.debug( 'EpostPaymentInformationStep.handleInactivatePaymentInfo()' );

		const action = find( this.state.componentData.modifyActions, {
			name: 'inactivate',
		} );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation.message ),
			iconName: 'close',
			confirmViewConfig: {
				type: 'paymentInfo',
				card: card
			}
		} );

		if ( shouldProceed ) {

			// cancel ongoing request
			this.epostPaymentInformationStepService.cancel();

			//hide preloader
			pageLevelPreloader.show();

			// set the address default
			const { status, data } = await this.epostPaymentInformationStepService.deletePaymentInfo( {
				url: action.url,
				data: {
					context: {
                        memberId: this.props.requestPayload.context.memberId,
                        patientId: this.props.requestPayload.context.patientId,
                        journey: this.props.requestPayload.context.journey
                    },
					data: { id: card.id }
				}
			} );

			//hide preloader
			pageLevelPreloader.hide();

			if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

				// update payment options
				const componentData = cloneDeep( this.state.componentData );

				const updatedList = map( componentData.paymentOptions, ( item ) => {
					if ( item.id === card.id ) {
						const updatedItem = cloneDeep( item );
						updatedItem.flags.isActivated = false;
						updatedItem.flags.isDefault = false;

						return updatedItem;
					}

					return item;
				} );
				
				componentData.paymentOptions = updatedList;

				this.setState( {
					componentData,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				} );
			} else {
				if( ! isEmpty( get( data, 'description' ) ) ) {
					errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
				} else {
					errorOverlay.open();
				}
			}
		}
	}

    async submitStepData( { url } ) {
        log.debug( 'EpostPaymentInformationStep.submitStepData()' );

        this.epostPaymentInformationStepService.cancel();

        // show preloader
        pageLevelPreloader.show();

        const { status, data } = await this.epostPaymentInformationStepService.submitStepData( {
            url,
            data: this.props.requestPayload
        } );

        // show preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // trigger success view
                MessageBus.trigger( MESSAGE_BUS_EVENTS.SHOW_EPOST_JOURNEY_SUCCESS_VIEW, { data } );

            } );

        } else {
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    async handleNavActions( { action } ) {
        log.debug( 'EpostPaymentInformationStep.handleNavActions()' );

        if( 'cancel' === action.name ) {

            if ( get( action, 'url' ) && get( this.props, 'requestPayload.context.reactivationFlowId' ) ) {
                const cancelStatus = await this.epostPaymentInformationStepService.cancelReactivationFlow( {
                    url: get( action, 'url' ),
                    data: this.props.requestPayload,
                } );
                if ( SERVICE_RESPONSE_STATUS.SUCCESS !== cancelStatus ) {
                    errorOverlay.open();
                    return;
                }
            }

            if( 'verification' === get( this.state, 'origin', null ) ) {
                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                    search: queryString.stringify( {
                        verifyOther: true,
                        step: 2,
                    } ),
                } );
            } else {
                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                    state: { memberId: this.state.memberId, patientId: this.state.patientId }
                } );
            }
        }

        if( 'update' === action.name ) {

            this.submitStepData( { url: get( action, 'url' ), config: get( action, 'config' ) } );
        }
    }

	/**
	 * submit hsaFsa toggle info
	 */
     async handleHsaFsaActionToggle( { url, id, flags }, callback = noop  ) {
		log.debug( 'EpostPaymentInformationStep.handleHsaFsaActionToggle()' );

		this.epostPaymentInformationStepService.cancel();

		// overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.epostPaymentInformationStepService.submitToggleHsaFsaPaymentInfoRequest( {
			url,
			data: {
				context: { 
					memberId: this.props.requestPayload.context.memberId, 
					patientId: this.props.requestPayload.context.patientId 
				},
				data: {
					id,
                    isHsaFsa: ! get( flags, 'isHsaFsa', false )
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isHsaFsa = ! get( flags, 'isHsaFsa', false );

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			}, callback );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}
    
    /*********************************************/

    /**
     * @desc Render a React view component
     */
    render() {

        // construct render redirection path for add payment
        const addPaymentRedirectPath = queryString.stringifyUrl( {
            url: this.props.location.pathname,
            query: {
                step: this.props.stepIndex + 1, // active tab
                reactivationFlowId: get( this.props, 'location.state.reactivationFlowId',
                    get( this.props, 'requestPayload.context.reactivationFlowId', undefined ) ),
            }
        } );

        return (
            <EpostPaymentInformationStepView
                isFetching={ this.state.isFetching }
                networkStatus={this.state.networkStatus }
				{...get( this.state, 'componentData', {} ) }
                onPaymentDefaultSelection={ this.handlePaymentDefaultSelection }
                onPaymemntInfoActivate={ this.handleActivatePaymentInfo }
                onPaymemntInfoInactivate = { this.handleInactivatePaymentInfo }
                onPaymemntInfoEdit = { this.handleEditPaymentInfo }
                addPaymentRedirectPath={ addPaymentRedirectPath }
                onNavActionClick={ this.handleNavActions }
                onHsaFsaActionToggle = { this.handleHsaFsaActionToggle }
			/>
        );
    }
}

// set display name
_EpostPaymentInformationStep.displayName = '_EpostPaymentInformationStep';

// set default props
_EpostPaymentInformationStep.defaultProps = {
	requestPayload: {},
};

// prop types
_EpostPaymentInformationStep.propTypes = {};

/*********************************************/

// with router functionality
export const EpostPaymentInformationStep = withRouter( _EpostPaymentInformationStep );


